// MARK://dhk8/admin-shell/components/AuditTrail
// Historial de auditoría de UNA entidad (I0). Se monta en el tab/sección "Historial"
// de cada ficha de detalle con <AuditTrail entityId={id} />. Lee auditLog por el grafo
// (platform-audit, tenant del JWT) vía clients/audit + el hook useAsync, y pinta un
// DataTable con: Cuándo · Acción · Qué cambió · Actor · Severidad + paginación.
// Estados: loading → Skeleton, error → banner, vacío → "Sin historial todavía".
// Helpers de formato compartidos en audit-format.ts (no duplicar con AuditPage).
import { useState } from 'react'
import * as audit from '../lib/clients/audit.js'
import { useAsync } from '../lib/use-async.js'
import DataTable, { type DataTableColumn } from './DataTable.js'
import StatusPill from './StatusPill.js'
import { SkeletonText } from './Skeleton.js'
import { formatTimestamp, humanizeEvent, renderChanges, severityTone, severityLabel, shortId } from './audit-format.js'

export interface AuditTrailProps {
  entityId: string
  eventName?: string
  pageSize?: number
}

export default function AuditTrail({ entityId, eventName, pageSize = 20 }: AuditTrailProps) {
  const [page, setPage] = useState(1)
  const res = useAsync(
    (s) => audit.listAuditLog(entityId, { eventName, page, limit: pageSize }, s),
    [entityId, eventName, page, pageSize],
  )

  const columns = auditColumns()

  if (res.loading && !res.data) {
    return (
      <div className="adm-card adm-card--pad">
        <SkeletonText lines={5} />
        <AuditStyles />
      </div>
    )
  }

  if (res.error) {
    return (
      <div className="adm-banner adm-banner--danger" role="alert">
        No se pudo cargar el historial: {res.error}
        <button className="adm-btn adm-btn--sm" style={{ marginLeft: 8 }} onClick={res.reload}>Reintentar</button>
      </div>
    )
  }

  const data = res.data
  const items = data?.items ?? []

  if (items.length === 0 && page === 1) {
    return (
      <div className="adm-empty">Sin historial todavía<AuditStyles /></div>
    )
  }

  return (
    <div>
      <DataTable<audit.AuditEntry>
        columns={columns}
        rows={items}
        getRowKey={(e) => e.id}
        loading={res.loading}
        emptyText="Sin registros en esta página."
        page={data?.page ?? page}
        pageSize={data?.limit ?? pageSize}
        total={data?.total ?? 0}
        onPageChange={setPage}
      />
      <AuditStyles />
    </div>
  )
}

// Columnas compartidas (las reutiliza AuditPage añadiéndole "Entidad").
export function auditColumns(): DataTableColumn<audit.AuditEntry>[] {
  return [
    {
      key: 'when',
      header: 'Cuándo',
      width: '150px',
      render: (e) => <span className="adm-mono" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatTimestamp(e.timestamp)}</span>,
    },
    {
      key: 'action',
      header: 'Acción',
      render: (e) => {
        const { action, domain } = humanizeEvent(e.eventName)
        return (
          <div className="adm-audit-action">
            <div className="adm-audit-action__main">{action}</div>
            {domain && <div className="adm-muted adm-audit-action__domain">{domain}</div>}
          </div>
        )
      },
    },
    {
      key: 'changes',
      header: 'Qué cambió',
      render: (e) => renderChanges(e.payload),
    },
    {
      key: 'actor',
      header: 'Actor',
      width: '160px',
      render: (e) => (
        <span className="adm-audit-actor">
          <span className="adm-badge">{e.actorType}</span>
          {e.actorId && <span className="adm-mono adm-muted adm-audit-actor__id">{shortId(e.actorId)}</span>}
        </span>
      ),
    },
    {
      key: 'severity',
      header: 'Severidad',
      width: '110px',
      render: (e) => <StatusPill tone={severityTone(e.severity)} label={severityLabel(e.severity)} />,
    },
  ]
}

// Estilos locales del trail (clases adm-*; tokens --pos-*; sin edge-strips).
export function AuditStyles() {
  return (
    <style>{`
      .adm-audit-action__main { font-weight: var(--pos-weight-medium, 500); }
      .adm-audit-action__domain { font-size: var(--pos-text-xxs, 11px); text-transform: uppercase; letter-spacing: var(--pos-tracking-wide, 0.04em); }
      .adm-audit-actor { display: inline-flex; align-items: center; gap: 6px; }
      .adm-audit-actor__id { font-size: var(--pos-text-xxs, 11px); }
      .adm-audit-changes { display: flex; flex-direction: column; gap: 2px; }
      .adm-audit-change { font-size: var(--pos-text-sm, 13px); line-height: var(--pos-leading-snug, 1.35); }
      .adm-audit-change__field { font-weight: var(--pos-weight-medium, 500); }
    `}</style>
  )
}
